import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ElementRef,
} from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { cn } from '@/shared/lib/cn';
import { useModalContext } from './modalContext';

/** Galaxy Popover — Radix-backed. Prefer for interactive contextual content. */

/**
 * Topmost open modal. Popover and menu panels also use role="dialog", so those
 * are skipped — otherwise the menu portals into itself and closes on the next click.
 */
export function openDialogPortalContainer(): HTMLElement | undefined {
  if (typeof document === "undefined") return undefined;
  const dialogs = document.querySelectorAll<HTMLElement>(
    '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]',
  );
  for (let i = dialogs.length - 1; i >= 0; i -= 1) {
    const dialog = dialogs[i];
    if (!dialog || dialog.closest("[data-radix-popper-content-wrapper]")) continue;
    return dialog;
  }
  return undefined;
}

/**
 * Hook to retrieve the exact portal container for the current modal context.
 * Falls back to topmost open modal/alertdialog or document.body if not in a modal.
 */
export function useDialogPortalContainer(): HTMLElement | undefined {
  const modal = useModalContext();
  if (modal?.container) {
    return modal.container;
  }
  return openDialogPortalContainer();
}

export const Popover = PopoverPrimitive.Root;
export const PopoverTrigger = PopoverPrimitive.Trigger;
export const PopoverAnchor = PopoverPrimitive.Anchor;
export const PopoverPortal = PopoverPrimitive.Portal;
export const PopoverClose = PopoverPrimitive.Close;

export const PopoverArrow = forwardRef<
  ElementRef<typeof PopoverPrimitive.Arrow>,
  ComponentPropsWithoutRef<typeof PopoverPrimitive.Arrow>
>(function PopoverArrow({ className, width = 14, height = 7, children, asChild, ...props }, ref) {
  if (asChild) {
    return (
      <PopoverPrimitive.Arrow
        ref={ref}
        asChild
        width={width}
        height={height}
        className={cn('-mt-[1px]', className)}
        {...props}
      >
        {children}
      </PopoverPrimitive.Arrow>
    );
  }

  return (
    <PopoverPrimitive.Arrow
      ref={ref}
      asChild
      width={width}
      height={height}
      className={cn('-mt-[1px]', className)}
      {...props}
    >
      <svg
        width={width}
        height={height}
        viewBox="0 0 30 10"
        preserveAspectRatio="none"
        className="block overflow-visible"
      >
        <path
          d="M 0 0 L 15 10 L 30 0"
          className="fill-surface stroke-border stroke-[1.25]"
        />
      </svg>
    </PopoverPrimitive.Arrow>
  );
});

export const PopoverContent = forwardRef<
  ElementRef<typeof PopoverPrimitive.Content>,
  ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(function PopoverContent({ className, align = 'start', sideOffset = 4, ...props }, ref) {
  const container = useDialogPortalContainer();
  return (
    <PopoverPrimitive.Portal container={container}>
      <PopoverPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        className={cn(
          'z-[var(--z-dropdown)] overflow-hidden rounded-control border border-border bg-surface shadow-2 outline-none',
          className,
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
});
